import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { useSEO, generateServiceSchema, generateBreadcrumbSchema } from '../../../utils/seo';

export default function HospitalDischarge() {
  const serviceSchema = generateServiceSchema(
    'Hospital Discharge Transportation',
    'Specialized hospital discharge transportation services coordinating with healthcare facilities for safe patient transport home or to care facilities. Professional, timely service throughout Orange County.'
  );
  
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/#services' },
    { name: 'Hospital Discharge', url: '/services/hospital-discharge' }
  ]);

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [serviceSchema, breadcrumbSchema]
  };

  useSEO({
    title: 'Hospital Discharge Transportation Westminster CA | Safe Patient Transport Home',
    description: 'Professional hospital discharge transportation in Westminster CA and Orange County. Safe, comfortable transport from hospital to home or care facility. Coordinated with healthcare providers.',
    keywords: 'hospital discharge transport, patient discharge transportation, hospital to home transport, medical facility discharge, Westminster CA hospital transport',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/hospital-discharge`,
    schema: combinedSchema
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/d55999c742bd56408ff2604d2819cfd4.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Hospital Discharge Transportation
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Safe, comfortable transport from hospital to home or care facility with professional assistance and medical supervision
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                variant="secondary" 
                size="lg"
                onClick={() => window.location.href = 'tel:+17147987318'}
                className="text-lg px-8 py-4"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Hospital Discharge Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Smooth Hospital-to-Home Transitions
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Hospital discharge can be overwhelming, especially when you're recovering from surgery, illness, or injury. InteCare's hospital discharge transportation service ensures a safe, comfortable journey from the hospital to your home or care facility.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our trained staff coordinates directly with hospital discharge planners and nursing staff to ensure continuity of care throughout the transport process. We handle all the logistics so you can focus on your recovery.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Direct coordination with hospital staff</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Medical equipment and supply transport</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Same-day and next-day availability</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://static.readdy.ai/image/d32fda172531ebc64455c87992c4a988/23f5b16500d6d9a0c620800d726832c2.png"
                alt="Hospital Discharge Process"
                className="w-full rounded-lg shadow-lg object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Types of Discharge Transport */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Discharge Transportation Options
            </h2>
            <p className="text-xl text-gray-600">
              Tailored transport services based on your recovery needs and medical condition
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-walk-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Ambulatory Discharge</h3>
              <p className="text-gray-600 mb-4">
                For patients who can walk but need assistance and support getting home safely after treatment.
              </p>
              <ul className="text-sm text-gray-500 text-left space-y-1">
                <li>• Minor surgery recovery</li>
                <li>• Outpatient procedures</li>
                <li>• Medication adjustments</li>
                <li>• Observation stays</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-wheelchair-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Wheelchair Discharge</h3>
              <p className="text-gray-600 mb-4">
                Wheelchair-accessible transport for patients with mobility limitations or restricted weight-bearing.
              </p>
              <ul className="text-sm text-gray-500 text-left space-y-1">
                <li>• Orthopedic surgery recovery</li>
                <li>• Leg/foot injuries</li>
                <li>• Balance issues</li>
                <li>• Temporary mobility aids</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hospital-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Stretcher Discharge</h3>
              <p className="text-gray-600 mb-4">
                For patients requiring continuous bed rest or those who cannot sit upright during transport.
              </p>
              <ul className="text-sm text-gray-500 text-left space-y-1">
                <li>• Major surgery recovery</li>
                <li>• Spinal conditions</li>
                <li>• Extended bed rest orders</li>
                <li>• Critical care transitions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Discharge Coordination Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Discharge Coordination Process
            </h2>
            <p className="text-xl text-gray-600">
              Seamless coordination with hospital staff for a smooth transition
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Pre-Discharge Planning</h3>
              <p className="text-gray-600">
                Work with discharge planners to schedule transport and understand medical needs and restrictions.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Medical Review</h3>
              <p className="text-gray-600">
                Review discharge orders, medications, and any special care instructions with nursing staff.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Safe Transfer</h3>
              <p className="text-gray-600">
                Professional staff assists with transfer from hospital bed to transport vehicle with medical supervision.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Home Delivery</h3>
              <p className="text-gray-600">
                Safe transport to destination with assistance getting settled and equipment/supplies delivered.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Transport */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              We Transport More Than Just Patients
            </h2>
            <p className="text-xl text-gray-600">
              Complete coordination of all your discharge needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Medical Equipment & Supplies</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <i className="ri-wheelchair-line text-blue-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Mobility Equipment</h4>
                    <p className="text-gray-600">Wheelchairs, walkers, crutches, and mobility aids</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <i className="ri-lungs-line text-green-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Respiratory Equipment</h4>
                    <p className="text-gray-600">Oxygen concentrators, CPAP machines, nebulizers</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <i className="ri-drop-line text-purple-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">IV & Medical Pumps</h4>
                    <p className="text-gray-600">IV pumps, feeding pumps, pain management devices</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <i className="ri-first-aid-kit-line text-orange-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Medical Supplies</h4>
                    <p className="text-gray-600">Dressings, medications, wound care supplies</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Personal Care & Comfort</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <i className="ri-shirt-line text-red-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Personal Belongings</h4>
                    <p className="text-gray-600">Clothing, personal items, flowers, and gifts</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <i className="ri-file-text-line text-teal-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Medical Documents</h4>
                    <p className="text-gray-600">Discharge papers, prescriptions, care instructions</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <i className="ri-medicine-bottle-line text-yellow-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Medications</h4>
                    <p className="text-gray-600">Prescription medications and pharmacy pickups</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mr-3 mt-1">
                    <i className="ri-heart-line text-indigo-600 text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Comfort Items</h4>
                    <p className="text-gray-600">Pillows, blankets, and other comfort accessories</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance & Billing */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Insurance Coverage for Hospital Discharge
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Most insurance plans cover medically necessary hospital discharge transportation. We work with your insurance to minimize out-of-pocket costs.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-blue-50 rounded-lg p-6">
                <i className="ri-hospital-line text-3xl text-blue-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Medicare Coverage</h3>
                <p className="text-gray-600">Medicare typically covers discharge transport when medically necessary and prescribed by a physician.</p>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6">
                <i className="ri-shield-line text-3xl text-green-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Insurance Benefits</h3>
                <p className="text-gray-600">Most private insurance plans include coverage for medical transportation services.</p>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-6">
                <i className="ri-file-text-line text-3xl text-purple-600 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Pre-Authorization</h3>
                <p className="text-gray-600">We handle insurance pre-authorization and billing paperwork for you.</p>
              </div>
            </div>
            
            <p className="text-gray-600">
              Our billing specialists work directly with your insurance provider to ensure maximum coverage and minimal hassle for you and your family.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready for Hospital Discharge?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            Let InteCare coordinate your safe transport home. We work directly with hospital staff to ensure a smooth, comfortable discharge process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => window.location.href = 'tel:+17147987318'}
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 text-lg px-8 py-4"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (714) 798-7318
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}